/**
 * Normalised API error.
 *
 * Wraps every non-OK response from our API into a single class so feature
 * code can pattern-match on `kind` instead of comparing strings.
 *
 * `code` is the stable machine-readable identifier from the server
 * (e.g. `ezjwt_bad_credentials`). `kind` is a coarser classification we
 * derive from HTTP status and known code prefixes.
 */
import { apiErrorBodySchema, type ApiErrorBody } from './schemas/auth'

export type ApiErrorKind =
  | 'bad_request' // 400 — malformed payload
  | 'unauthorized' // 401 — missing/invalid credentials, expired token
  | 'forbidden' // 403 — policy refused (incl. CSRF mismatch)
  | 'not_found' // 404
  | 'conflict' // 409 — link policy clash etc.
  | 'rate_limited' // 429 — `Retry-After` is parsed
  | 'unavailable' // 503 — issuance / downstream subsystem failed
  | 'timeout' // request exceeded the client timeout (server hung / too slow)
  | 'network' // request never reached the server (DNS, CORS, offline)
  | 'parse' // server response could not be parsed into our schema
  | 'unknown'

export interface ApiErrorInit {
  kind: ApiErrorKind
  status: number
  code: string
  message: string
  retryAfter?: number
  hint?: string
  /** Request URL (path or absolute) — for the debug pane. */
  url?: string
  /** Request HTTP method — for the debug pane. */
  method?: string
  /**
   * Raw server response body verbatim. WP REST often carries useful
   * context here under `data.params` (e.g. `{ status: "publish,draft
   * is not one of publish, draft, …" }`) that's not in `message`.
   */
  details?: unknown
  cause?: unknown
}

export class ApiError extends Error {
  readonly kind: ApiErrorKind
  readonly status: number
  readonly code: string
  readonly retryAfter: number | undefined
  readonly hint: string | undefined
  readonly url: string | undefined
  readonly method: string | undefined
  readonly details: unknown

  constructor(init: ApiErrorInit) {
    super(init.message, init.cause ? { cause: init.cause } : undefined)
    this.name = 'ApiError'
    this.kind = init.kind
    this.status = init.status
    this.code = init.code
    this.retryAfter = init.retryAfter
    this.hint = init.hint
    this.url = init.url
    this.method = init.method
    this.details = init.details
  }

  isAuthExpired(): boolean {
    return (
      this.kind === 'unauthorized' &&
      (this.code === 'ezjwt_token_expired' || this.code === 'ezjwt_invalid_token')
    )
  }

  isCsrfMismatch(): boolean {
    return this.kind === 'forbidden' && this.code === 'ezjwt_csrf_mismatch'
  }
}

function kindFromStatus(status: number): ApiErrorKind {
  if (status === 400) return 'bad_request'
  if (status === 401) return 'unauthorized'
  if (status === 403) return 'forbidden'
  if (status === 404) return 'not_found'
  if (status === 409) return 'conflict'
  if (status === 429) return 'rate_limited'
  if (status === 503) return 'unavailable'
  if (status === 0) return 'network'
  return 'unknown'
}

function parseRetryAfter(value: string | null): number | undefined {
  if (!value) return undefined
  const seconds = Number(value)
  return Number.isFinite(seconds) && seconds >= 0 ? seconds : undefined
}

interface MapInput {
  status: number
  body: unknown
  headers?: Headers | Record<string, string>
  url?: string
  method?: string
}

function getHeader(headers: MapInput['headers'], name: string): string | null {
  if (!headers) return null
  if (headers instanceof Headers) return headers.get(name)
  const lower = name.toLowerCase()
  for (const [k, v] of Object.entries(headers)) {
    if (k.toLowerCase() === lower) return v
  }
  return null
}

/**
 * Detect WP REST's "auth issue disguised as a 400" pattern.
 *
 * `wp/v2/<cpt>?status=draft` is validated at param-parse time, BEFORE
 * the auth check turns a missing capability into 401. So the same
 * underlying "you can't read drafts" surfaces as:
 *  - 401 for endpoints that don't take `status` → standard refresh-retry
 *  - 400 `rest_invalid_param` with `params.status === 'rest_forbidden_status'`
 *    for endpoints that do → escapes the 401-retry path
 *
 * We reclassify the 400 as `unauthorized` so the api client's existing
 * refresh-and-retry plumbing picks it up. Logged-in users without the
 * capability will still see the 401 after retry, but at least the
 * "expired token in a long-open tab" case recovers transparently.
 */
function isStatusForbiddenIn400(body: unknown): boolean {
  if (!body || typeof body !== 'object') return false
  const b = body as { code?: unknown; data?: unknown }
  if (b.code !== 'rest_invalid_param') return false
  const data = b.data as { params?: unknown } | undefined
  const params = data?.params as { status?: unknown } | undefined
  // The 'status' param specifically came back rejected.
  return typeof params?.status === 'string'
}

/**
 * Convert any non-OK response into an ApiError.
 * Falls back gracefully when the server body is not in our envelope.
 */
export function toApiError(input: MapInput): ApiError {
  const parsed = apiErrorBodySchema.safeParse(input.body)
  const body: ApiErrorBody = parsed.success
    ? parsed.data
    : { code: 'http_error', message: `HTTP ${input.status}` }

  // Reclassify the WP "status-forbidden disguised as 400" pattern as
  // `unauthorized` so refresh-retry picks it up. See the helper above
  // for the rationale.
  const reclassifyAuth = input.status === 400 && isStatusForbiddenIn400(input.body)

  const init: ApiErrorInit = {
    kind: reclassifyAuth ? 'unauthorized' : kindFromStatus(input.status),
    status: input.status,
    code: body.code,
    message: body.message,
  }
  if (body.hint !== undefined) init.hint = body.hint
  if (input.url !== undefined) init.url = input.url
  if (input.method !== undefined) init.method = input.method
  if (input.body !== undefined) init.details = input.body
  const retryAfter = parseRetryAfter(getHeader(input.headers, 'retry-after'))
  if (retryAfter !== undefined) init.retryAfter = retryAfter

  return new ApiError(init)
}

interface NetworkErrorContext {
  url?: string
  method?: string
}

/** Network-level failure (request never reached the server). */
export function networkError(cause: unknown, ctx: NetworkErrorContext = {}): ApiError {
  const init: ApiErrorInit = {
    kind: 'network',
    status: 0,
    code: 'network_error',
    message: cause instanceof Error ? cause.message : 'Network error',
    cause,
  }
  if (ctx.url !== undefined) init.url = ctx.url
  if (ctx.method !== undefined) init.method = ctx.method
  return new ApiError(init)
}

/**
 * Client-side timeout — the request exceeded the configured deadline
 * (server hung or is too slow). Distinct from `network` so the UI can
 * say "took too long, try again" rather than "offline".
 */
export function timeoutError(cause: unknown, ctx: NetworkErrorContext = {}): ApiError {
  const init: ApiErrorInit = {
    kind: 'timeout',
    status: 0,
    code: 'request_timeout',
    message: 'The request took too long. Please try again.',
    cause,
  }
  if (ctx.url !== undefined) init.url = ctx.url
  if (ctx.method !== undefined) init.method = ctx.method
  return new ApiError(init)
}

/** Schema validation failure when server returned 2xx but body shape was wrong. */
export function parseError(cause: unknown): ApiError {
  return new ApiError({
    kind: 'parse',
    status: 0,
    code: 'response_parse_error',
    message: 'Unexpected response shape from the server',
    cause,
  })
}

/**
 * Extract a user-presentable string from anything thrown — `ApiError`'s
 * message when present, the generic `Error.message` otherwise, and a
 * sensible fallback for non-Error rejections (raw strings, undefined,
 * `{ code: ..., message: ... }`-shaped bodies escaped out of a `catch`).
 *
 * Centralised because the same `instanceof ApiError ? ... :
 * instanceof Error ? ... : 'Unknown'` ladder used to live inline at
 * ~15 call sites — easy to forget a branch, easy to phrase
 * differently in each.
 */
export function messageOf(err: unknown, fallback = 'Unknown error'): string {
  if (err instanceof ApiError) return err.message
  if (err instanceof Error) return err.message
  if (typeof err === 'string' && err.trim()) return err
  return fallback
}
